import { Link } from "react-router-dom";
import { cn } from "@/lib/cn";

const VARIANTS = {
  primary:
    "bg-brand-600 text-white shadow-soft hover:bg-brand-500 active:bg-brand-700",
  secondary:
    "bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200",
  outline:
    "border border-slate-300 bg-transparent text-slate-700 hover:bg-slate-100 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/5",
  ghost:
    "bg-transparent text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5",
  glass:
    "glass text-slate-800 hover:bg-white/90 dark:text-white dark:hover:bg-white/10",
  // Solid white button for placing ON a brand/gradient background (e.g. the CTA).
  // A dedicated variant avoids overriding the default's text-white via className —
  // cn() doesn't tailwind-merge, so an override would leave text-white applied and
  // render white-on-white (invisible) text.
  onbrand:
    "bg-white text-brand-700 shadow-soft hover:bg-brand-50",
  // Translucent companion for a secondary action on the same brand background.
  onbrandGhost:
    "bg-white/10 text-white ring-1 ring-white/30 hover:bg-white/20",
  // Destructive confirmation (delete a project, remove a member). Call sites were
  // already passing variant="danger"; with no entry here cn() dropped it and the
  // irreversible action rendered as unstyled text.
  danger:
    "bg-rose-600 text-white shadow-soft hover:bg-rose-500 active:bg-rose-700",
};

const SIZES = {
  sm: "h-9 px-3.5 text-sm gap-1.5",
  md: "h-11 px-5 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2.5",
};

// Polymorphic button: renders a router <Link> when `to` is set, an <a> when
// `href` is set, otherwise a <button>.
export default function Button({
  variant = "primary",
  size = "md",
  to,
  href,
  className,
  children,
  disabled,
  ...props
}) {
  const classes = cn(
    "inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 disabled:cursor-not-allowed disabled:opacity-60",
    VARIANTS[variant],
    SIZES[size],
    className
  );

  // `disabled` is meaningless on an anchor — it renders as an unknown DOM
  // attribute and the link still navigates, so a "disabled" CTA stayed clickable.
  // Degrade to an inert <span> that looks the same instead of lying about it.
  if (disabled && (to || href)) {
    return (
      <span aria-disabled="true" className={cn(classes, "cursor-not-allowed opacity-60")}>
        {children}
      </span>
    );
  }

  if (to) {
    return (
      <Link to={to} className={classes} {...props}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} {...props}>
        {children}
      </a>
    );
  }
  return (
    <button className={classes} disabled={disabled} {...props}>
      {children}
    </button>
  );
}
